import { useEffect, useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { BrBox } from "@/components/BrBox";
import { BrBtn } from "@/components/BrBtn";
import { BrLabel } from "@/components/BrLabel";
import { BrTag } from "@/components/BrTag";
import { Logo } from "@/components/Logo";
import { PaletteSwitcher } from "@/components/PaletteSwitcher";
import {
  useConnectChesscom,
  useMe,
  useStartLichess,
  useUpdateMe,
  offsetShadow,
  TYPE,
} from "@chess/shared";
import { useToast } from "@/components/ToastProvider";
import { useTheme, useThemeControls } from "@/theme/ThemeProvider";
import { useViewport } from "@/util/viewport";

export function ConnectAccountPage() {
  const p = useTheme();
  const { isMobile } = useViewport();
  const nav = useNavigate();
  const { data: me, refetch } = useMe();
  const connectChesscom = useConnectChesscom();
  const startLichess = useStartLichess();
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const { themeId, setThemeId } = useThemeControls();
  const updateMe = useUpdateMe();

  useEffect(() => {
    if (searchParams.get("lichess") === "linked") {
      toast.push({ kind: "success", text: "Lichess account linked." });
      refetch();
      const next = new URLSearchParams(searchParams);
      next.delete("lichess");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams, refetch, toast]);

  const chesscomAccount = me?.connected_accounts.find((a) => a.provider === "chesscom");
  const lichessAccount = me?.connected_accounts.find((a) => a.provider === "lichess");

  const [username, setUsername] = useState("");

  async function handleChesscom(e: FormEvent) {
    e.preventDefault();
    try {
      await connectChesscom.mutateAsync({ username: username.trim() });
      toast.push({ kind: "success", text: "Chess.com linked — syncing games" });
      await refetch();
    } catch (err) {
      toast.push({ kind: "danger", text: err instanceof Error ? err.message : "Failed to link" });
    }
  }

  async function handleLichess() {
    try {
      const { authorize_url } = await startLichess.mutateAsync();
      window.location.href = authorize_url;
    } catch (err) {
      toast.push({ kind: "danger", text: err instanceof Error ? err.message : "Failed to start Lichess OAuth" });
    }
  }

  const inputStyle = {
    width: "100%",
    background: p.bg,
    color: p.fg,
    border: `2px solid ${p.border}`,
    padding: "12px 14px",
    fontFamily: TYPE.mono,
    fontSize: 14,
    fontWeight: 700,
    outline: "none",
  } as const;

  const canContinue = !!(chesscomAccount || lichessAccount);

  return (
    <div style={{ minHeight: "100vh", background: p.bg, color: p.fg, fontFamily: TYPE.body, padding: 16 }}>
      <div style={{ maxWidth: 720, margin: "40px auto" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: isMobile ? 22 : 26, fontWeight: 800, letterSpacing: -0.5, marginBottom: 18 }}>
          <Logo size={36} />
          GAMBIT.
        </div>
        <BrBox padding={isMobile ? 16 : 20} shadowSize={4} style={{ marginBottom: 18 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
            <div>
              <BrLabel>PICK A LOOK</BrLabel>
              <div style={{ fontSize: 13.5, fontWeight: 700, marginTop: 4 }}>
                You can change this any time from settings.
              </div>
            </div>
            <PaletteSwitcher
              themeId={themeId}
              onChange={(id) => {
                setThemeId(id);
                updateMe.mutate({ theme: id });
              }}
              compact={isMobile}
            />
          </div>
        </BrBox>

        <BrLabel>STEP 2 OF 2</BrLabel>
        <h1
          style={{
            fontFamily: TYPE.display,
            fontSize: isMobile ? 40 : 56,
            fontWeight: 800,
            letterSpacing: -2,
            lineHeight: 0.95,
            margin: "6px 0 22px",
            textTransform: "uppercase",
          }}
        >
          Connect your<br />games.
        </h1>

        <BrBox padding={isMobile ? 18 : 22} shadowSize={6} style={{ marginBottom: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
            <div
              style={{
                width: 48,
                height: 48,
                background: p.success,
                color: p.successFg,
                border: `2px solid ${p.border}`,
                boxShadow: offsetShadow(p, 2, 2),
                display: "grid",
                placeItems: "center",
                fontSize: 24,
                fontWeight: 800,
                flexShrink: 0,
              }}
            >
              ♚
            </div>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ fontSize: 16, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.3 }}>CHESS.COM</div>
              <div style={{ fontSize: 12, color: p.fgMuted, fontFamily: TYPE.mono, marginTop: 2 }}>
                {chesscomAccount ? `Linked as ${chesscomAccount.external_username}` : "Public-API import · no password required"}
              </div>
            </div>
            {chesscomAccount && <BrTag bg={p.success} fg={p.successFg}>● LINKED</BrTag>}
          </div>
          {!chesscomAccount && (
            <form onSubmit={handleChesscom} style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
              <input value={username} onChange={(e) => setUsername(e.target.value)} placeholder="chess.com username" style={{ ...inputStyle, flex: 1, minWidth: 200 }} required />
              <BrBtn type="submit" variant="accent" disabled={connectChesscom.isPending || username.trim().length === 0}>
                {connectChesscom.isPending ? "Linking…" : "Connect →"}
              </BrBtn>
            </form>
          )}
        </BrBox>

        <BrBox padding={isMobile ? 18 : 22} shadowSize={6} style={{ marginBottom: 22 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
            <div
              style={{
                width: 48,
                height: 48,
                background: p.info,
                color: p.infoFg,
                border: `2px solid ${p.border}`,
                boxShadow: offsetShadow(p, 2, 2),
                display: "grid",
                placeItems: "center",
                fontSize: 24,
                fontWeight: 800,
                flexShrink: 0,
              }}
            >
              ♛
            </div>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ fontSize: 16, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.3 }}>LICHESS</div>
              <div style={{ fontSize: 12, color: p.fgMuted, fontFamily: TYPE.mono, marginTop: 2 }}>
                {lichessAccount ? `Linked as ${lichessAccount.external_username}` : "OAuth · we'll redirect you to lichess.org"}
              </div>
            </div>
            {lichessAccount ? (
              <BrTag bg={p.success} fg={p.successFg}>● LINKED</BrTag>
            ) : (
              <BrBtn variant="surface" onClick={handleLichess} disabled={startLichess.isPending} style={{ background: p.info, color: p.infoFg }}>
                {startLichess.isPending ? "Opening…" : "Connect →"}
              </BrBtn>
            )}
          </div>
        </BrBox>

        <div style={{ display: "flex", gap: 10, justifyContent: "space-between", alignItems: "center", flexWrap: "wrap" }}>
          <BrBtn variant="ghost" withShadow={false} onClick={() => nav("/app")}>Skip for now</BrBtn>
          <BrBtn variant="primary" size="lg" onClick={() => nav("/app")} disabled={!canContinue}>
            Continue to dashboard →
          </BrBtn>
        </div>
      </div>
    </div>
  );
}
